import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Checkbox, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import { Query } from 'src/components/atoms/query';
import { CardWithContent } from 'src/components/molecules/card-with-content';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { CompactPagination } from 'src/components/organisms/compact-pagination';
import { useAssignSpacesToSpaceGroup } from 'src/features/space-groups/api/hooks/use-assign-spaces-to-space-group';
import { useCreateSpaceGroup } from 'src/features/space-groups/api/hooks/use-create-space-group';
import { fetchSpaces } from 'src/features/spaces/api/lib/spaces';
import { spacesKeys } from 'src/features/spaces/api/query-keys/spaces';
import { spaceTypesTranslations } from 'src/features/spaces/constants/space-types-translations';
import { SearchState } from 'src/hooks/meilisearch/types/search-state';
import { useMeilisearch } from 'src/hooks/meilisearch/use-meilisearch';
import { usePagination } from 'src/hooks/use-pagination';
import { useSearchState } from 'src/hooks/use-search-state';
import { useUserStore } from 'src/store/user-store';
import { z } from 'zod';
import { SpaceFloorFilterPopover } from '../../../molecules/space-floor-filter-popover';
import { SpaceTypeFilterPopover } from '../../../molecules/space-type-filter-popover';
import { useCreateSpaceGroupFormValidationSchema } from './schema/use-form-validation-schema';

interface Props extends DialogActions {}

export const CreateSpaceGroupDialog = ({ open, onCancel }: Props) => {
  const { t } = useTranslation(['spaceGroups', 'spaces']);

  const { directoryToken } = useUserStore();

  const formValidationSchema = useCreateSpaceGroupFormValidationSchema();

  type FormValues = z.infer<typeof formValidationSchema>;

  const defaultValues: FormValues = {
    name: '',
    spaceTokens: [],
  };

  const { control, handleSubmit, reset, watch, setValue } = useForm<FormValues>(
    {
      resolver: zodResolver(formValidationSchema),
      defaultValues,
      mode: 'onBlur',
    },
  );

  const initialFilterValue = [
    'spaceGroupToken NOT EXISTS OR spaceGroupToken IS NULL',
  ];

  const [searchState, setSearchState] = useSearchState({
    filter: initialFilterValue,
  });

  const {
    page,
    perPage,
    changePage,
    changePerPage,
    setTotalItems,
    totalPages,
    nextDisabled,
    nextPage,
    prevDisabled,
    prevPage,
  } = usePagination();

  const { results, isLoading, isFetching, error } = useMeilisearch({
    fetch: fetchSpaces,
    page,
    perPage,
    queryKey: spacesKeys.list(directoryToken ?? '', perPage, page, searchState),
    searchState,
    enabled: open,
  });

  useEffect(() => {
    if (!results) return;
    setTotalItems(results.estimatedTotalHits);
  }, [results]);

  const handleOnCancel = () => {
    onCancel?.();
    reset(defaultValues);
    setSearchState({
      q: '',
      filter: [initialFilterValue],
      sort: [],
    });
  };

  const { mutateAsync: createSpaceGroup, isPending: isCreatingSpaceGroup } =
    useCreateSpaceGroup(handleOnCancel);
  const {
    mutate: assignSpacesToSpaceGroup,
    isPending: isAssigningSpacesToSpaceGroup,
  } = useAssignSpacesToSpaceGroup();

  const handleChangeQuery = (query: string) => {
    setSearchState(prevState => ({
      ...prevState,
      q: query,
    }));
  };

  const handleChangeFilterValue = (filters: SearchState['filter']) => {
    setSearchState(prevState => ({
      ...prevState,
      filter: [[...initialFilterValue], ...filters],
    }));
  };

  const spaceTokens = watch('spaceTokens');

  const handleUpdateSelectedSpaces = (token: string) => {
    if (spaceTokens.includes(token)) {
      setValue(
        'spaceTokens',
        spaceTokens.filter(item => item !== token),
      );

      return;
    }

    setValue('spaceTokens', [...spaceTokens, token]);
  };

  const handleCreateSpaceGroup = async (data: FormValues) => {
    const token = await createSpaceGroup(data.name);

    if (token && data.spaceTokens.length > 0) {
      assignSpacesToSpaceGroup({
        spaceGroupToken: token,
        spacesTokens: data.spaceTokens,
      });
    }
  };

  return (
    <Dialog
      open={open}
      title={t('spaceGroups:common.addNewGroup')}
      onCancel={handleOnCancel}
      onConfirm={handleSubmit(handleCreateSpaceGroup)}
      isConfirmButtonSubmitting={
        isCreatingSpaceGroup || isAssigningSpacesToSpaceGroup
      }>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Controller
          control={control}
          name="name"
          render={({
            field: { name, value, onChange, onBlur },
            fieldState: { error, invalid },
          }) => (
            <InputField
              name={name}
              label={t('spaceGroups:common.createGroupDialog.name')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={invalid}
              helperText={error?.message}
              required
            />
          )}
        />

        <Typography variant="body2">
          {t('spaceGroups:common.createGroupDialog.selectSpaces')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
          }}>
          <Query
            searchAsType
            disabled={false}
            onChange={handleChangeQuery}
            sx={{
              minWidth: 200,
            }}
            value={searchState.q}
          />

          <SpaceTypeFilterPopover
            filters={searchState.filter}
            onChange={handleChangeFilterValue}
          />

          <SpaceFloorFilterPopover
            filters={searchState.filter}
            onChange={handleChangeFilterValue}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}>
          {results?.hits?.map(space => (
            <CardWithContent
              key={space.token}
              isSelected={spaceTokens.includes(space.token)}
              onClick={() => {
                handleUpdateSelectedSpaces(space.token);
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox checked={spaceTokens.includes(space.token)} />
                <Box>
                  <Typography variant="body2">{space.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('common.createGroupDialog.spaceDescription', {
                      spaceType: t(spaceTypesTranslations[space.type]),
                      floor: space.floor,
                    })}
                  </Typography>
                </Box>
              </Box>
            </CardWithContent>
          ))}

          <DataStatusIndicator
            dataLength={results?.hits?.length ?? 0}
            isFetching={isLoading}
            error={error?.response?.data?.message}
            resourceUnavailableText={t(
              'spaceGroups:common.createGroupDialog.noSpaces',
            )}
          />

          <CompactPagination
            disabled={isLoading}
            isFetching={isFetching}
            page={page}
            changePage={changePage}
            handlePerPageChange={changePerPage}
            nextDisabled={nextDisabled}
            nextPage={nextPage}
            prevDisabled={prevDisabled}
            prevPage={prevPage}
            perPage={perPage}
            totalPages={totalPages}
          />
        </Box>
      </Box>
    </Dialog>
  );
};
